<!-- RecipientSelectField.vue -->
<template>
    <div class="element RecipientSelectField" :data-required="required" :class="{ 'error' : !!error.error, 'readonly': (!this.editing || this.readonly) }">
        <label :for="`field_${id}`">{{ label }}</label>

        <var v-if="preview">{{ input }}</var>

        <!-- Zichtbare tag editor -->
        <div class="email-tags" @click="focusInput">
      <span
          v-for="(tag, i) in tags"
          :key="tag + '-' + i"
          class="email-tag"
          :title="tag"
      >
        {{ tag }}
        <button v-if="this.editing && !this.readonly" type="button" class="remove" @click.stop="remove(i)" aria-label="Verwijder">×</button>
      </span>

            <input
                ref="inputEl"
                v-if="this.editing && !this.readonly"
                v-model="inputValue"
                :placeholder="!tags.length ? trans(placeholder) : ''"
                class="email-input"
                type="text"
                @keydown="onKeydown"
                @blur="onBlur"
                @paste="onPaste"
                autocomplete="off"
                spellcheck="false"
            />
        </div>

        <!-- Verborgen veld met JSON-array (v-model / form submit) -->
        <input
            type="hidden"
            :id="`field_${id}`"
            :name="name"
            v-model="input"
        />

        <div v-if="error.error" class="error">{{ trans(error.message) }}</div>
    </div>
</template>

<script>
import { ref, watch, computed, onMounted } from "vue";

// Form helpers
import useFormValidation from "./_validation";
import { propDefaults } from "./_props";

// Helpers
import { mergeDeep } from "helpers/_objects";
import trans from "helpers/_translation";

export default {
    name: "RecipientSelectField",
    props: mergeDeep(propDefaults, {
        // lokale opties voor dit veld
        allowDuplicates: { type: Boolean, default: false },
        max: { type: Number, default: Infinity },
        validateOnBlur: { type: Boolean, default: true },
        placeholder: { type: String, default: "Voer e-mailadressen in…" },
        modelValue: { type: Array, default: undefined }, // optioneel v-model array
    }),
    emits: ["update:modelValue", "change", "add", "remove"],
    setup(props, { emit }) {
        const { validateTextField, error } = useFormValidation();

        // Verborgen input (JSON-string)
        const input = ref("");

        // Tag editor state
        const tags = ref([]);
        const inputValue = ref("");
        const inputEl = ref(null);

        // Validatie
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;
        const delimiters = [",", ";", " "];
        const atLimit = computed(() => tags.value.length >= props.max);

        const toJson = (arr) => JSON.stringify(arr);
        const parseArray = (v) => {
            if (Array.isArray(v)) return v;
            if (typeof v === "string" && v.trim()) {
                try {
                    const parsed = JSON.parse(v);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    return [];
                }
            }
            return [];
        };

        function focusInput() {
            inputEl.value?.focus();
        }

        function add(email) {
            if (!email) return false;
            const e = email.trim();
            if (!emailRe.test(e)) return false;
            if (atLimit.value) return false;
            if (!props.allowDuplicates && tags.value.includes(e)) return false;
            tags.value.push(e);
            emit("add", e);
            syncHidden();
            validateInput();
            return true;
        }

        function remove(index) {
            const [removed] = tags.value.splice(index, 1);
            emit("remove", removed);
            syncHidden();
            validateInput();
        }

        function tokenize({ force = false } = {}) {
            if (!inputValue.value) return;

            const parts = inputValue.value.split(/[,\s;]+/).filter(Boolean);
            let consumedAny = false;

            for (const part of parts) consumedAny = add(part) || consumedAny;

            if (consumedAny) {
                inputValue.value = "";
                return;
            }

            if (force && add(inputValue.value)) {
                inputValue.value = "";
            }
        }

        function onKeydown(e) {
            const key = e.key;
            const isDelimiterKey = key === "Enter" || key === "Tab" || delimiters.includes(key);

            if (isDelimiterKey) {
                const pre = inputValue.value;
                tokenize();
                if (pre !== inputValue.value) e.preventDefault();
            }

            if (key === "Backspace" && !inputValue.value && tags.value.length) {
                e.preventDefault();
                remove(tags.value.length - 1);
            }
        }

        function onBlur() {
            if (props.validateOnBlur) tokenize({ force: true });
        }

        function onPaste(e) {
            const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
            if (!text) return;
            e.preventDefault();
            inputValue.value += (inputValue.value ? " " : "") + text;
            tokenize();
        }

        function syncHidden() {
            const unique = [...new Set(tags.value.filter((x) => emailRe.test(x)).map((x) => x.trim()))];
            const asJson = toJson(unique);
            if (input.value !== asJson) input.value = asJson;
            // emit array-vorm naar buiten (optioneel v-model)
            emit("update:modelValue", unique);
            emit("change", unique);
        }

        function restoreFrom(valueLike) {
            const arr = parseArray(valueLike).map((x) => `${x}`.trim()).filter((x) => x && emailRe.test(x));
            tags.value = [...new Set(arr)];
            syncHidden();
        }

        function validateInput() {
            if (props.required) {
                // validatie op basis van tags (niet op verborgen string)
                const ok = tags.value.length > 0;
                if (!ok) validateTextField(props.label, ""); // triggert melding
                else error.error = false;
            }
        }

        // Init: props.value -> props.default_value -> (optioneel) props.modelValue
        onMounted(() => {
            const initial =
                props.value !== undefined
                    ? props.value
                    : props.default_value !== undefined
                        ? props.default_value
                        : props.modelValue !== undefined
                            ? props.modelValue
                            : [];
            restoreFrom(initial);
            validateInput();
        });

        // Externe updates (value / default_value / modelValue)
        watch(
            () => props.value,
            (v) => {
                if (v === undefined) return;
                restoreFrom(v);
            }
        );
        watch(
            () => props.default_value,
            (v) => {
                if (v === undefined) return;
                // alleen als er nog niks ingevuld is
                if (!tags.value.length) restoreFrom(v);
            }
        );
        watch(
            () => props.modelValue,
            (v) => {
                if (v === undefined) return;
                restoreFrom(v);
            },
            { deep: true }
        );

        return {
            trans,
            // props via template
            id: props.id,
            name: props.name,
            label: props.label,
            required: props.required,
            preview: props.preview,

            // state
            input,
            error,

            tags,
            inputValue,
            inputEl,
            atLimit,

            // methods
            focusInput,
            add,
            remove,
            onKeydown,
            onBlur,
            onPaste,
        };
    },
};
</script>